/**
 * 全局 fetch：MyEMS API 会话失效时清除登录 Cookie 并跳转登录页。
 * 需在应用入口最早 import，且只安装一次。
 */
import { APIBaseURL } from '../config';

const AUTH_COOKIE_NAMES = ['is_logged_in', 'user_name', 'user_display_name', 'user_uuid', 'token'];

/** 与 myems-api Falcon HTTPError description 一致（勿匹配登录失败类错误） */
const SESSION_EXPIRED_DESCRIPTIONS = new Set([
  'API.USER_SESSION_TIMEOUT',
  'API.USER_SESSION_NOT_FOUND',
  'API.ADMINISTRATOR_SESSION_NOT_FOUND',
  'API.ADMINISTRATOR_SESSION_TIMEOUT',
  'API.INVALID_SESSION_PLEASE_RE_LOGIN',
  'API.NEW_USER_SESSION_TIMEOUT',
  'API.NEW_USER_SESSION_NOT_FOUND',
  'API.API_KEY_HAS_EXPIRED',
  'API.INVALID_TOKEN'
]);

function eraseCookie(name) {
  document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
}

function clearAuthCookies() {
  AUTH_COOKIE_NAMES.forEach(eraseCookie);
}

function getRequestUrl(input) {
  if (typeof input === 'string') return input;
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  return '';
}

function isMyemsApiRequest(url) {
  if (!url) return false;
  const base = String(APIBaseURL || '').replace(/\/$/, '');
  if (!base) return false;
  return url.startsWith(base);
}

function collectDescriptionStrings(body) {
  if (!body || typeof body !== 'object') return [];
  const out = [];
  if (typeof body.description === 'string') out.push(body.description);
  if (typeof body.title === 'string') out.push(body.title);
  if (Array.isArray(body.errors)) {
    body.errors.forEach(e => {
      if (typeof e === 'string') out.push(e);
      if (e && typeof e === 'object' && typeof e.description === 'string') out.push(e.description);
    });
  }
  return out;
}

function bodyIndicatesSessionExpired(body) {
  return collectDescriptionStrings(body).some(s => SESSION_EXPIRED_DESCRIPTIONS.has(s));
}

function redirectToLogin() {
  if (window.__myemsSessionRedirecting) return;
  window.__myemsSessionRedirecting = true;
  clearAuthCookies();
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const path = `${base}/authentication/basic/login`.replace(/\/+/g, '/');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  window.location.assign(normalized);
}

function shouldInspectErrorResponse(status) {
  return status === 400 || status === 401 || status === 403 || status === 404;
}

async function maybeHandleSessionExpired(response, requestUrl) {
  if (response.ok || !isMyemsApiRequest(requestUrl)) return;
  if (!shouldInspectErrorResponse(response.status)) return;
  if (response.status === 401) {
    redirectToLogin();
    return;
  }
  let data;
  try {
    const text = await response.clone().text();
    if (!text) return;
    data = JSON.parse(text);
  } catch (e) {
    return;
  }
  if (bodyIndicatesSessionExpired(data)) {
    redirectToLogin();
  }
}

function install() {
  if (typeof window === 'undefined' || window.__myemsFetchGuardInstalled) return;
  window.__myemsFetchGuardInstalled = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const requestUrl = getRequestUrl(input);
    const response = await nativeFetch(input, init);
    await maybeHandleSessionExpired(response, requestUrl);
    return response;
  };
}

install();
